/*
 * The proposals list's views (the design's tabs) and each row's next step.
 *
 * Each view is a server filter of `crmProposals` (ENT-565, tabs kept by the owner 2026-11-02), and its
 * count is the matching field of `crmProposalCounts`, so a tab's badge always equals what the tab
 * lists. "Needs action" is the server's own rule (`filter.attention`): drafts, plus approved proposals
 * with no open signing request. "Approved" holds every approved and accepted proposal, so an approved
 * proposal that still has to be sent is in both, on purpose.
 */

export interface ProposalViewFilter {
  attention?: boolean
  states?: string[]
}

export interface ProposalViewDef {
  id: string
  label: string
  /** The server filter the view sends. */
  filter: ProposalViewFilter
  /** The lifecycle states the view can hold, for its status select. Empty means every state. */
  states: readonly string[]
  /** The `crmProposalCounts` field that counts this view. */
  countKey: 'attention' | 'withManager' | 'approved' | 'all'
}

const DRAFT_STATES = ['AWAITING_INPUT', 'INPUT_IN_PROGRESS', 'INPUT_SUBMITTED', 'RENDERED'] as const

export const PROPOSAL_VIEWS = [
  { id: 'attention', label: 'Needs action', filter: { attention: true }, states: [...DRAFT_STATES, 'APPROVED'], countKey: 'attention' },
  { id: 'review', label: 'Awaiting approval', filter: { states: ['PENDING_APPROVAL'] }, states: ['PENDING_APPROVAL'], countKey: 'withManager' },
  // Accepted is the terminal form of an approved proposal, so it remains in the same user-facing view.
  { id: 'approved', label: 'Approved', filter: { states: ['APPROVED', 'ACCEPTED'] }, states: ['APPROVED', 'ACCEPTED'], countKey: 'approved' },
  { id: 'all', label: 'All', filter: {}, states: [], countKey: 'all' },
] as const satisfies readonly ProposalViewDef[]

export type ProposalViewId = (typeof PROPOSAL_VIEWS)[number]['id']

export const DEFAULT_PROPOSAL_VIEW: ProposalViewId = 'attention'

export function isProposalViewId(value: unknown): value is ProposalViewId {
  return typeof value === 'string' && PROPOSAL_VIEWS.some((v) => v.id === value)
}

/** Old Accepted links now open the combined Approved view instead of falling back to the default. */
export function normaliseProposalViewId(value: unknown): ProposalViewId | undefined {
  if (isProposalViewId(value)) return value
  return value === 'accepted' ? 'approved' : undefined
}

export function proposalView(id: ProposalViewId): ProposalViewDef {
  return PROPOSAL_VIEWS.find((v) => v.id === id) ?? PROPOSAL_VIEWS[0]
}

/**
 * The view a URL means. A link with a filter and no tab is a link from before the tabs existed: it
 * was the whole list with that filter, so it stays that.
 */
export function resolveProposalView(search: { tab?: string; state?: string; q?: string; jurisdiction?: string }): ProposalViewId {
  const tab = normaliseProposalViewId(search.tab)
  if (tab) return tab
  return search.state || search.q || search.jurisdiction ? 'all' : DEFAULT_PROPOSAL_VIEW
}

export type ProposalNextStepTarget = 'edit' | 'detail'
export type ProposalNextStepIcon = 'edit' | 'file' | 'shield' | 'send' | 'retry' | 'alert' | 'eye'

export interface ProposalNextStep {
  label: string
  /** `edit` is the inputs form; `detail` is the proposal's own page, where the action is. */
  target: ProposalNextStepTarget
  icon: ProposalNextStepIcon
}

const VIEW_STEP: ProposalNextStep = { label: 'View', target: 'detail', icon: 'eye' }

/** Signing ended without a signature: the proposal goes back for a new round. */
export const ENDED_UNSIGNED: readonly string[] = ['DECLINED', 'EXPIRED', 'VOIDED']

/**
 * What the sales person does next, from the lifecycle state and the latest signing delivery's state.
 * The server still decides what is allowed; every step but "Continue draft" opens the proposal page,
 * where the action is.
 */
export function proposalNextStep(state: string, signingState?: string | null): ProposalNextStep {
  switch (state) {
    case 'AWAITING_INPUT':
    case 'INPUT_IN_PROGRESS':
      return { label: 'Continue draft', target: 'edit', icon: 'edit' }
    case 'INPUT_SUBMITTED':
      return { label: 'Generate PDF', target: 'detail', icon: 'file' }
    case 'RENDERED':
      return { label: 'Request approval', target: 'detail', icon: 'shield' }
    case 'APPROVED':
      if (!signingState || signingState === 'SUPERSEDED') return { label: 'Send for signature', target: 'detail', icon: 'send' }
      if (signingState === 'REQUEST_FAILED') return { label: 'Retry request', target: 'detail', icon: 'retry' }
      if (signingState === 'REQUEST_UNCONFIRMED') return { label: 'Check status', target: 'detail', icon: 'alert' }
      if (ENDED_UNSIGNED.includes(signingState)) return { label: 'Revise proposal', target: 'detail', icon: 'edit' }
      return VIEW_STEP
    default:
      return VIEW_STEP
  }
}
